import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusMessage, Skeleton } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { WORK_QUEUE_PATHS } from '../../constants/inboxViews.js';
import { History, TriangleAlert } from 'lucide-react';

/** Content-sized side card matching the audit block chrome. */
function AuditCard({ children }) {
  return <div className="rounded-[10px] border border-edge bg-surface px-4 py-4">{children}</div>;
}

const AuditTrailRow = memo(function AuditTrailRow({ entry }) {
  return (
    <li className="flex items-baseline justify-between gap-3 text-[11.5px] leading-snug">
      <p className="min-w-0">
        <span className="font-semibold text-content">{entry.actor}</span>{' '}
        <span className="text-content-muted">{entry.action}</span>
      </p>
      <span className="shrink-0 text-[10.5px] text-content-faint">
        {entry.timestamp ?? entry.timeAgo}
      </span>
    </li>
  );
});

/**
 * Audit Trail — content-sized side card (aims-htl `.wqep-side-block`), not a
 * full-height shell. Rows sit inside the card; “View full audit →” navigates to
 * the Activity Log tab. Parent column scrolls with `scrollbar-aimsos-auto-sm`
 * when cards overflow.
 */
function AuditTrailTab({ entries = [], loading = false, error = null }) {
  const { t } = useTranslation('work-queues-app');
  const navigate = useNavigate();

  if (loading) {
    return (
      <AuditCard>
        <ul className="flex flex-col gap-2.5" aria-busy="true" aria-live="polite">
          {['sk-1', 'sk-2', 'sk-3'].map((key) => (
            <li key={key} className="flex items-center justify-between gap-3">
              <Skeleton height={12} width="65%" />
              <Skeleton height={12} width={40} />
            </li>
          ))}
        </ul>
      </AuditCard>
    );
  }

  if (error) {
    return (
      <AuditCard>
        <StatusMessage icon={TriangleAlert} title={t('audit.errorTitle')} subtitle={error} />
      </AuditCard>
    );
  }

  if (entries.length === 0) {
    return (
      <AuditCard>
        <StatusMessage icon={History} title={t('audit.emptyTitle')} subtitle={t('audit.emptySubtitle')} />
      </AuditCard>
    );
  }

  return (
    <div className="rounded-[10px] border border-edge bg-surface px-[18px] py-4">
      <ul className="flex flex-col gap-2">
        {entries.map((entry) => (
          <AuditTrailRow key={entry.id} entry={entry} />
        ))}
      </ul>

      <button
        type="button"
        onClick={() => navigate(WORK_QUEUE_PATHS.activity)}
        className="mt-3 w-fit text-left text-xs text-content-muted transition-colors hover:text-content"
      >
        {t('audit.viewFull')}
      </button>
    </div>
  );
}

export default AuditTrailTab;
